import { useEffect, useMemo, useState } from 'react';
import { Check, X, CreditCard, Download } from 'lucide-react';
import { clsx } from 'clsx';
import {
  acceptRetentionOffer,
  cancelSubscription,
  finalizeMockCheckoutSession,
  createCheckoutSession,
  createPortalSession,
  getBillingPlans,
  getBillingSubscription,
  listBillingInvoices,
  pauseSubscription,
  requestRefund,
  runMockBillingValidation,
  triggerMockBillingWebhook,
  type BillingCycle,
  type BillingCurrency,
  type BillingInvoice,
  type BillingPlan,
  type BillingSubscription,
} from '@/api/billing';
import { getUsageSnapshot, clearUsageSnapshotCache } from '@/api/usage';
import { useTranslation } from 'react-i18next';
import { notifications } from '@/utils/notifications';
import { useAuth } from '@/auth/AuthContext';
import { useAccountRole } from '@/hooks/useAccountRole';
import { SettingsReadOnlyBanner } from '@/components/settings/SettingsReadOnlyBanner';
import { useEffectiveViewport } from '@/hooks/useEffectiveViewport';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { QuotaMeter } from '@/components/billing/QuotaMeter';
import { formatStorageBytes } from '@trove/note-sanitize';

// --- Types ---

type UiPlan = BillingPlan & { isCurrent?: boolean };

type PendingMockCheckout = {
  sessionId: string;
  planName: string;
  amount: number;
  billingCycle: BillingCycle;
  currency: BillingCurrency;
};

// --- Sub-components ---

const featureLabelKeys: Record<string, string> = {
  bookmark: 'subscription.featureLabels.bookmark',
  notes: 'subscription.featureLabels.notes',
  users: 'subscription.featureLabels.users',
  highlighter: 'subscription.featureLabels.highlighter',
  sessionbookmarkgroup: 'subscription.featureLabels.sessionbookmarkgroup',
  tags: 'subscription.featureLabels.tags',
  taskstag: 'subscription.featureLabels.taskstag',
  groups: 'subscription.featureLabels.groups',
  tasks: 'subscription.featureLabels.tasks',
  workspace: 'subscription.featureLabels.workspace',
  ai_search: 'subscription.featureLabels.ai_search',
  ai_assistant: 'subscription.featureLabels.ai_assistant',
  ai_suggest: 'subscription.featureLabels.ai_suggest',
  noteMediaStorage: 'subscription.featureLabels.noteMediaStorage',
  maxNoteBodyBytes: 'subscription.featureLabels.maxNoteBodyBytes',
};

const BYTE_FEATURE_KEYS = new Set(['noteMediaStorage', 'maxNoteBodyBytes']);

function formatPlanFeatureValue(key: string, value: number | boolean | string): string | number {
  if (typeof value === 'number' && BYTE_FEATURE_KEYS.has(key)) {
    return formatStorageBytes(value);
  }
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  return value;
}

function formatCurrency(currency: BillingCurrency, amount: number) {
  const locale = currency === 'inr' ? 'en-IN' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(amount);
}

function invoiceDate(ts: number) {
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Build a mailto: URL that prefills the support refund-request flow.
// Policy lives in `docs/PRICING.md §5` and the support macros in
// `docs/LAUNCH_OPERATIONS.md §5.4`.
const SUPPORT_REFUND_EMAIL = 'support@trovepile.com';

function buildRefundMailto(
  row: BillingInvoice,
  email: string | undefined,
  subjectTemplate: string,
  bodyTemplate: string
): string {
  const fmt = (tpl: string, vars: Record<string, string>) =>
    Object.entries(vars).reduce(
      (acc, [k, v]) => acc.split(`{${k}}`).join(v),
      tpl,
    );
  const vars = {
    invoiceId: row.invoiceId,
    amount: formatCurrency(row.currency, row.amount),
    date: invoiceDate(row.createdAt),
    email: email ?? '',
  };
  const subject = fmt(subjectTemplate, vars);
  const body = fmt(bodyTemplate, vars);
  return `mailto:${SUPPORT_REFUND_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const BillingInvoiceMobileRow = ({
  row,
  actionLoading,
  onRefund,
  refundMailto,
}: {
  row: BillingInvoice;
  actionLoading: boolean;
  onRefund: (invoiceId: string) => void;
  refundMailto: string;
}) => {
  const { t } = useTranslation('settings');

  return (
    <div className="px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{invoiceDate(row.createdAt)}</p>
          <p className="mt-1 text-base font-black text-foreground">{formatCurrency(row.currency, row.amount)}</p>
        </div>
        <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-muted-foreground">
          {row.status}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {row.status === 'paid' && (
          <a
            href={refundMailto}
            className="inline-flex min-h-10 items-center rounded-xl border border-border px-4 text-xs font-black uppercase tracking-wider text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
          >
            {t('subscription.requestRefund')}
          </a>
        )}
        {row.status === 'paid' && (
          <button
            type="button"
            onClick={() => onRefund(row.invoiceId)}
            disabled={actionLoading}
            className="min-h-10 rounded-xl border border-border px-4 text-xs font-black uppercase tracking-wider text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-60"
          >
            {t('subscription.refund')}
          </button>
        )}
        <button
          type="button"
          className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-border px-4 text-xs font-black uppercase tracking-wider text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
        >
          <Download size={14} />
          {t('subscription.download')}
        </button>
      </div>
    </div>
  );
};

function getDisplayAmount(plan: BillingPlan | null, currency: BillingCurrency, cycle: BillingCycle): number {
  if (!plan) return 0;
  const monthly = currency === 'inr' ? plan.prices.inrMonthly : plan.prices.usdMonthly;
  return cycle === 'yearly' ? Math.round(monthly * 12 * 0.8) : monthly;
}

const PlanCard = ({
  plan,
  billingCycle,
  currency,
  loading,
  onSelect,
  readOnly = false,
}: {
  plan: UiPlan;
  billingCycle: BillingCycle;
  currency: BillingCurrency;
  loading: boolean;
  onSelect: (planId: number) => void;
  readOnly?: boolean;
}) => {
  const { t } = useTranslation('settings');
  const monthly = currency === 'inr' ? plan.prices.inrMonthly : plan.prices.usdMonthly;
  const displayPrice = billingCycle === 'yearly' ? Math.round(monthly * 12 * 0.8) : monthly;
  const priceSuffix = billingCycle === 'yearly' ? t('subscription.perYear') : t('subscription.perMonth');
  const features = Object.entries(plan.features).map(([key, value]) => ({
    key,
    label: featureLabelKeys[key] ? t(featureLabelKeys[key]) : key,
    value,
    included: value > 0,
  }));
  return (
  <div className={clsx(
    'relative z-0 flex flex-col rounded-2xl border p-4 transition-all sm:rounded-3xl sm:p-6',
    plan.isCurrent
      ? 'z-10 border-primary bg-card ring-2 ring-primary/20 md:scale-[1.02]'
      : 'bg-card/60 border-border hover:border-foreground/20'
  )}>
    {plan.isCurrent && (
      <div className="absolute -top-2.5 left-1/2 z-[1] -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-[9px] font-black uppercase tracking-[0.2em] text-white sm:-top-3 sm:px-4 sm:py-1 sm:text-[10px]">
        {t('subscription.currentPlan')}
      </div>
    )}

    <div className="space-y-1.5 sm:space-y-2">
      <h4 className="text-lg font-black text-foreground sm:text-xl">{plan.name}</h4>
      <p className="min-h-[28px] text-xs font-medium leading-relaxed text-muted-foreground sm:min-h-[32px]">
        {plan.key === 'free'
          ? t('subscription.planDescriptions.free')
          : plan.id === 1
            ? t('subscription.planDescriptions.pro')
            : plan.id === 2
              ? t('subscription.planDescriptions.team')
              : t('subscription.planDescriptions.business')}
      </p>
    </div>

    <div className="mt-4 flex items-baseline gap-1 sm:mt-6">
      <span className="text-2xl font-black text-foreground sm:text-3xl">{formatCurrency(currency, displayPrice)}</span>
      <span className="text-base font-bold text-muted-foreground">{priceSuffix}</span>
    </div>

    {!readOnly ? (
    <button 
      type="button"
      disabled={plan.isCurrent || loading}
      onClick={() => onSelect(plan.id)}
      className={clsx(
        'mt-5 min-h-11 w-full rounded-xl py-2.5 text-sm font-black transition-all active:scale-[0.98] sm:mt-7 sm:min-h-0 sm:py-3',
        plan.isCurrent 
          ? "bg-muted text-muted-foreground cursor-default border border-border" 
          : plan.id >= 2
            ? "bg-primary text-primary-foreground hover:bg-primary/90" 
            : "bg-muted hover:bg-muted/80 text-foreground border border-border"
      )}
    >
      {plan.isCurrent ? t('subscription.currentPlan') : plan.isContactSales ? t('subscription.contactSales') : t('subscription.selectPlan')}
    </button>
    ) : null}

    <div className="mt-5 space-y-2.5 sm:mt-8 sm:space-y-3.5">
      {features.map((feature) => (
        <div key={feature.key} className={clsx(
          'flex items-center gap-2 text-xs font-medium transition-colors sm:gap-2.5',
          feature.included ? 'text-foreground/80' : 'text-muted-foreground/40 line-through opacity-50'
        )}>
          {feature.included ? (
            <Check size={16} className="shrink-0 text-primary" />
          ) : (
            <X size={16} className="shrink-0" />
          )}
          <span className="min-w-0 break-words">
            {feature.label}
            {typeof feature.value !== 'boolean' ? (
              <>: {feature.included ? formatPlanFeatureValue(feature.key, feature.value) : 0}</>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  </div>
  );
};

// --- Main Page Section ---

export const SubscriptionSettings = () => {
  const { t } = useTranslation('settings');
  const { t: tToast } = useTranslation('notifications');
  const { user } = useAuth();
  const { canManageAccount } = useAccountRole();
  const readOnlyOrg = !canManageAccount;
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [currency, setCurrency] = useState<BillingCurrency>('usd');
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [subscription, setSubscription] = useState<BillingSubscription | null>(null);
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [lastCheckoutSessionId, setLastCheckoutSessionId] = useState<string | null>(null);
  const [validationReport, setValidationReport] = useState<Array<{ step: string; ok: boolean; details?: string }>>([]);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [pauseSubmitting, setPauseSubmitting] = useState(false);
  const [pauseMonths, setPauseMonths] = useState<1 | 2 | 3>(1);
  const [retentionSubmitting, setRetentionSubmitting] = useState(false);
  const [cancelSurveyReason, setCancelSurveyReason] = useState('');
  const [retentionOfferAccepted, setRetentionOfferAccepted] = useState(false);
  const { isWide: isBillingTableWide } = useEffectiveViewport();
  const [seatUsage, setSeatUsage] = useState<{ used: number; max: number } | null>(null);
  const [pendingCheckout, setPendingCheckout] = useState<PendingMockCheckout | null>(null);
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [usageRefreshKey, setUsageRefreshKey] = useState(0);
  const isDev = import.meta.env.DEV;

  const refreshQuotaMeters = () => {
    clearUsageSnapshotCache('quota');
    setUsageRefreshKey((k) => k + 1);
  };

  const loadBilling = async (refreshQuotas = false) => {
    if (refreshQuotas) clearUsageSnapshotCache('quota');
    setLoading(true);
    try {
      const [catalog, subData, invData] = await Promise.all([
        getBillingPlans(),
        getBillingSubscription(),
        listBillingInvoices(),
      ]);
      setPlans(catalog);
      setSubscription(subData.subscription);
      setInvoices(invData);
      const seatMax = catalog.find((p) => p.id === subData.subscription.planId)?.features.users ?? 0;
      if (seatMax > 0) {
        try {
          const usage = await getUsageSnapshot(refreshQuotas, { view: 'quota' });
          const bucket = usage.resources?.users;
          if (bucket) setSeatUsage({ used: bucket.used, max: bucket.max });
        } catch {
          setSeatUsage(null);
        }
      } else {
        setSeatUsage(null);
      }
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : tToast('subscription.loadBillingFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadBilling();
  }, []);

  useEffect(() => {
    if (!subscription) return;
    setBillingCycle(subscription.billingCycle);
    setCurrency(subscription.currency);
  }, [subscription]);

  const plansForUi = useMemo<UiPlan[]>(() => {
    const visiblePlans = plans.filter((p) => p.key !== 'business');
    if (!subscription) return visiblePlans as UiPlan[];
    return visiblePlans.map((p) => ({ ...p, isCurrent: p.id === subscription.planId }));
  }, [plans, subscription]);

  const currentPlan = useMemo(() => plans.find((p) => p.id === subscription?.planId) ?? null, [plans, subscription]);

  const nextBill = subscription?.currentPeriodEnd ? invoiceDate(subscription.currentPeriodEnd) : t('subscription.na');

  const handlePlanSelect = async (planId: number) => {
    if (!subscription) return;
    const selected = plans.find((p) => p.id === planId);
    if (!selected || selected.isContactSales) return;
    const isSameSelection =
      selected.id === subscription.planId &&
      subscription.billingCycle === billingCycle &&
      subscription.currency === currency;
    if (isSameSelection) return;
    setActionLoading(true);
    try {
      const { checkoutUrl, sessionId, isMockCheckout } = await createCheckoutSession(
        planId,
        billingCycle,
        currency
      );
      setLastCheckoutSessionId(sessionId);
      const mockFlow = isMockCheckout ?? checkoutUrl.includes('mock-stripe.trovepile.local');
      if (mockFlow) {
        setPendingCheckout({
          sessionId,
          planName: selected.name,
          amount: getDisplayAmount(selected, currency, billingCycle),
          billingCycle,
          currency,
        });
        return;
      }
      notifications.success(tToast('subscription.redirectingCheckout'));
      window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : tToast('subscription.startCheckoutFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleManagePlan = async () => {
    setActionLoading(true);
    try {
      const { portalUrl } = await createPortalSession();
      window.open(portalUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : tToast('subscription.openPortalFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const executeCancelAtPeriodEnd = async () => {
    setCancelSubmitting(true);
    try {
      await cancelSubscription(false, {
        cancelSurveyReason: cancelSurveyReason.trim() || undefined,
        retentionOfferShown: true,
        retentionOfferAccepted: retentionOfferAccepted || undefined,
      });
      notifications.success(tToast('subscription.cancelAtPeriodEnd'));
      setCancelModalOpen(false);
      setCancelSurveyReason('');
      setRetentionOfferAccepted(false);
      await loadBilling(true);
      refreshQuotaMeters();
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : tToast('subscription.cancelSubscriptionFailed'));
    } finally {
      setCancelSubmitting(false);
    }
  };

  const executePauseWithSelectedMonths = async () => {
    setPauseSubmitting(true);
    try {
      await pauseSubscription(pauseMonths);
      notifications.success(tToast('subscription.pauseSuccess'));
      setCancelModalOpen(false);
      await loadBilling(true);
      refreshQuotaMeters();
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : tToast('subscription.pauseFailed'));
    } finally {
      setPauseSubmitting(false);
    }
  };

  const executeAcceptRetentionDiscount = async () => {
    setRetentionSubmitting(true);
    try {
      await acceptRetentionOffer();
      notifications.success(tToast('subscription.retentionOfferSuccess'));
      setCancelModalOpen(false);
      await loadBilling(true);
      refreshQuotaMeters();
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : tToast('subscription.retentionOfferFailed'));
    } finally {
      setRetentionSubmitting(false);
    }
  };

  const switchToProFromCancelModal = async () => {
    setCancelModalOpen(false);
    await handlePlanSelect(1);
  };

  const executeMockCheckout = async () => {
    if (!pendingCheckout || !user?.aid) return;
    setCheckoutSubmitting(true);
    try {
      await finalizeMockCheckoutSession(pendingCheckout.sessionId, user!.aid, user?.email);
      notifications.success(tToast('subscription.planUpdated'));
      setPendingCheckout(null);
      await loadBilling(true);
      refreshQuotaMeters();
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : tToast('subscription.completeCheckoutFailed'));
    } finally {
      setCheckoutSubmitting(false);
    }
  };

  const handleRequestRefund = async (invoiceId: string) => {
    setActionLoading(true);
    try {
      await requestRefund(invoiceId, 'user_requested');
      notifications.success(tToast('subscription.refundProcessedMock'));
      await loadBilling(true);
      refreshQuotaMeters();
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : tToast('subscription.refundFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleMockWebhook = async (
    eventType:
      | 'checkout.session.completed'
      | 'invoice.paid'
      | 'invoice.payment_failed'
      | 'customer.subscription.updated'
      | 'customer.subscription.deleted'
      | 'charge.refunded'
      | 'charge.disputed'
  ) => {
    if (!user?.aid) {
      notifications.error(tToast('subscription.accountContextMissingWebhook'));
      return;
    }
    setActionLoading(true);
    try {
      const payload: Record<string, unknown> = {
        reason: eventType === 'invoice.payment_failed' ? t('subscription.cardDeclinedMock') : undefined,
      };
      if (eventType === 'checkout.session.completed') {
        if (!lastCheckoutSessionId) {
          notifications.error(tToast('subscription.createCheckoutFirst'));
          return;
        }
        payload.sessionId = lastCheckoutSessionId;
      }
      await triggerMockBillingWebhook(eventType, user.aid, payload);
      notifications.success(tToast('subscription.mockEventProcessed', { eventType }));
      await loadBilling(true);
      refreshQuotaMeters();
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : tToast('subscription.mockEventFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRunValidation = async () => {
    setActionLoading(true);
    try {
      const result = await runMockBillingValidation();
      setValidationReport(result.report ?? []);
      if (result.status === 'success') {
        notifications.success(result.message ?? tToast('subscription.billingValidationPassed'));
      } else {
        notifications.error(result.message ?? tToast('subscription.billingValidationFailed'));
      }
      await loadBilling(true);
      refreshQuotaMeters();
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : tToast('subscription.billingValidationError'));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="max-w-5xl animate-in space-y-5 pb-10 fade-in slide-in-from-bottom-2 duration-500 sm:space-y-8 sm:pb-14 lg:space-y-10">
      {readOnlyOrg ? <SettingsReadOnlyBanner /> : null}
      {/* Header */}
      <div className="space-y-1.5 sm:space-y-2">
        <h2 className="text-2xl font-black tracking-tight text-foreground sm:text-3xl">{t('subscription.title')}</h2>
        <p className="text-xs font-medium leading-snug text-muted-foreground sm:text-sm sm:leading-relaxed">
          {t('subscription.subtitle')}
        </p>
      </div>

      {/* Top Section: Active Plan */}
      <div className="grid grid-cols-1 items-start gap-4 sm:gap-6">
        <div className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:rounded-3xl sm:p-6">
           <div className="min-w-0 space-y-2 sm:space-y-3">
              <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                 <h3 className="text-xl font-black text-foreground sm:text-2xl">{currentPlan?.name ?? t('subscription.planFallback')}</h3>
                 <span
                   className={`rounded-full border px-2.5 py-0.5 text-[9px] font-black uppercase tracking-widest sm:px-3 sm:py-1 sm:text-[10px] ${
                     subscription?.status === 'paused'
                       ? 'border-sky-500/25 bg-sky-500/10 text-sky-600'
                       : subscription?.status === 'pendingCancel'
                         ? 'border-amber-500/25 bg-amber-500/10 text-amber-600'
                         : 'border-emerald-500/20 bg-emerald-500/10 text-emerald-500'
                   }`}
                 >
                   {subscription?.status === 'paused'
                     ? t('subscription.statusPaused')
                     : subscription?.status === 'pendingCancel'
                       ? t('subscription.statusPendingCancel')
                       : t('subscription.active')}
                 </span>
              </div>
              <p className="max-w-sm text-xs font-medium leading-snug text-muted-foreground sm:text-sm sm:leading-relaxed">
                 {t('subscription.nextBillPrefix')}{' '}
                 <span className="text-foreground">{formatCurrency(subscription?.currency ?? currency, getDisplayAmount(currentPlan, subscription?.currency ?? currency, subscription?.billingCycle ?? billingCycle))}</span>{' '}
                 {t('subscription.nextBillOn')}{' '}
                 <span className="text-foreground italic">{nextBill}</span>.
              </p>
              {subscription?.status === 'past_due' && (
                <p className="text-xs font-semibold text-destructive sm:text-sm">
                  {t('subscription.paymentFailed')}: {subscription.failureReason ?? t('subscription.updatePaymentMethod')}
                </p>
              )}
              {(subscription?.cancelAtPeriodEnd || subscription?.status === 'pendingCancel') && (
                <p className="text-xs font-semibold text-amber-600 sm:text-sm">
                  {subscription?.status === 'pendingCancel'
                    ? t('subscription.pendingCancelInfo')
                    : t('subscription.cancelAtPeriodEnd')}
                </p>
              )}
              {subscription?.status === 'paused' && subscription.pauseUntil != null && (
                <p className="text-xs font-semibold text-sky-600 sm:text-sm">
                  {t('subscription.pausedInfo', { date: invoiceDate(subscription.pauseUntil) })}
                </p>
              )}
              {seatUsage && seatUsage.max > 0 && (
                <p className="text-xs font-medium text-muted-foreground sm:text-sm">
                  {t('subscription.seatUsage', { used: seatUsage.used, max: seatUsage.max })}
                  {seatUsage.used >= seatUsage.max && (
                    <span className="ml-1 font-semibold text-amber-600">{t('subscription.seatUsageAtCap')}</span>
                  )}
                </p>
              )}
              {subscription?.retentionCouponExpiresAt != null &&
                subscription.retentionCouponExpiresAt > Date.now() && (
                  <p className="text-xs font-semibold text-emerald-600 sm:text-sm">
                    {t('subscription.retentionCouponActive', {
                      date: invoiceDate(subscription.retentionCouponExpiresAt),
                    })}
                  </p>
                )}
              {subscription?.status === 'canceled' && (
                <p className="text-xs font-semibold text-muted-foreground sm:text-sm">
                  {t('subscription.canceledInfo')}
                </p>
              )}
              {!readOnlyOrg ? (
              <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                 <button
                   type="button"
                   onClick={handleManagePlan}
                   disabled={loading || actionLoading}
                   className="min-h-11 w-full rounded-xl bg-primary px-5 py-2.5 text-sm font-black text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.98] disabled:opacity-60 sm:w-auto sm:px-6"
                 >
                    {t('subscription.managePlan')}
                 </button>
                 {subscription?.status !== 'canceled' &&
                   subscription?.status !== 'paused' &&
                   subscription?.status !== 'pendingCancel' && (
                   <button
                     type="button"
                     onClick={() => setCancelModalOpen(true)}
                     disabled={loading || actionLoading}
                     className="flex min-h-11 w-full items-center justify-center text-center text-sm font-black text-muted-foreground transition-colors hover:text-red-500 disabled:opacity-60 sm:min-h-0 sm:w-auto sm:justify-start"
                   >
                       {t('subscription.cancelSubscription')}
                   </button>
                 )}
              </div>
              ) : null}
           </div>
        </div>
      </div>

      <section className="space-y-4 rounded-2xl border border-border bg-card p-4 sm:rounded-3xl sm:p-6">
        <h3 className="text-lg font-black tracking-tight text-foreground sm:text-xl">{t('subscription.usageOverview')}</h3>
        <QuotaMeter refreshKey={usageRefreshKey} />
      </section>

      {/* Available Plans */}
      <section className="space-y-4 pt-1 sm:space-y-6">
        <div className="flex flex-col gap-3 px-0.5 sm:px-1 md:flex-row md:items-end md:justify-between md:gap-4">
          <h3 className="text-lg font-black tracking-tight text-foreground sm:text-xl">{t('subscription.availablePlans')}</h3>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
            <div className="flex w-full items-center gap-1 rounded-xl border border-border bg-muted p-1 sm:w-auto">
             <button
               type="button"
               onClick={() => setBillingCycle('monthly')}
               className={clsx(
                 'min-h-10 flex-1 rounded-lg px-3 py-2 text-xs font-black transition-all sm:min-h-0 sm:flex-none sm:px-4',
                 billingCycle === 'monthly'
                   ? 'bg-primary text-primary-foreground'
                   : 'text-muted-foreground hover:text-foreground'
               )}
             >
               {t('subscription.monthly')}
             </button>
             <button
               type="button"
               onClick={() => setBillingCycle('yearly')}
               className={clsx(
                 'flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-black transition-all sm:min-h-0 sm:flex-none sm:gap-2 sm:px-4',
                 billingCycle === 'yearly'
                   ? 'bg-primary text-primary-foreground'
                   : 'text-muted-foreground hover:text-foreground'
               )}
             >
               {t('subscription.yearly')}{' '}
                <span
                  className={clsx(
                    'text-[10px] font-black',
                    billingCycle === 'yearly' ? 'text-primary-foreground/85' : 'text-primary'
                  )}
                >
                  ({t('subscription.save20')})
                </span>
             </button>
          </div>
            <div className="flex w-full items-center gap-1 rounded-xl border border-border bg-muted p-1 sm:w-auto">
            <button
              type="button"
              onClick={() => setCurrency('usd')}
              className={clsx(
                'min-h-10 flex-1 rounded-lg px-3 py-2 text-xs font-black transition-all sm:min-h-0 sm:flex-none',
                currency === 'usd'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              USD
            </button>
            <button
              type="button"
              onClick={() => setCurrency('inr')}
              className={clsx(
                'min-h-10 flex-1 rounded-lg px-3 py-2 text-xs font-black transition-all sm:min-h-0 sm:flex-none',
                currency === 'inr'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              INR
            </button>
          </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3">
           {plansForUi.map(plan => (
             <PlanCard
               key={plan.id}
               plan={plan}
               billingCycle={billingCycle}
               currency={currency}
               loading={loading || actionLoading}
               onSelect={handlePlanSelect}
               readOnly={readOnlyOrg}
             />
           ))}
        </div>
      </section>

      {isDev && (
        <section className="space-y-3 sm:space-y-4">
          <h3 className="text-lg font-black tracking-tight text-foreground sm:text-xl">{t('subscription.devMockBillingEvents')}</h3>
          <div className="flex flex-col gap-2 rounded-2xl border border-border bg-card p-3 sm:flex-row sm:flex-wrap sm:gap-2.5 sm:p-4">
            <button
              type="button"
              onClick={() => handleMockWebhook('checkout.session.completed')}
              disabled={actionLoading || !lastCheckoutSessionId}
              className="min-h-10 rounded-xl border border-border px-4 py-2 text-left text-xs font-black text-primary transition-colors hover:border-primary/40 disabled:opacity-60 sm:text-center"
            >
              {t('subscription.completeLastCheckout')}
            </button>
            <button
              type="button"
              onClick={() => handleMockWebhook('invoice.payment_failed')}
              disabled={actionLoading}
              className="min-h-10 rounded-xl border border-border px-4 py-2 text-left text-xs font-black text-destructive transition-colors hover:border-destructive/40 disabled:opacity-60 sm:text-center"
            >
              {t('subscription.triggerPaymentFailed')}
            </button>
            <button
              type="button"
              onClick={() => handleMockWebhook('customer.subscription.deleted')}
              disabled={actionLoading}
              className="min-h-10 rounded-xl border border-border px-4 py-2 text-left text-xs font-black text-muted-foreground transition-colors hover:border-muted-foreground/40 disabled:opacity-60 sm:text-center"
            >
              {t('subscription.triggerSubscriptionDeleted')}
            </button>
            <button
              type="button"
              onClick={() => handleMockWebhook('invoice.paid')}
              disabled={actionLoading}
              className="min-h-10 rounded-xl border border-border px-4 py-2 text-left text-xs font-black text-primary transition-colors hover:border-primary/40 disabled:opacity-60 sm:text-center"
            >
              {t('subscription.triggerInvoicePaid')}
            </button>
            <button
              type="button"
              onClick={handleRunValidation}
              disabled={actionLoading}
              className="min-h-10 rounded-xl border border-border px-4 py-2 text-left text-xs font-black text-foreground transition-colors hover:border-foreground/40 disabled:opacity-60 sm:text-center"
            >
              {t('subscription.runBillingValidation')}
            </button>
          </div>
          {validationReport.length > 0 && (
            <div className="mt-3 rounded-xl border border-border bg-muted/20 p-3 sm:mt-4 sm:p-4">
              <p className="mb-2 text-xs font-black uppercase tracking-wider text-muted-foreground sm:mb-3">{t('subscription.validationReport')}</p>
              <ul className="space-y-2">
                {validationReport.map((row) => (
                  <li key={row.step} className="break-words text-xs">
                    <span className={clsx('mr-2 font-black', row.ok ? 'text-emerald-500' : 'text-destructive')}>
                      {row.ok ? t('subscription.pass') : t('subscription.fail')}
                    </span>
                    <span className="font-semibold text-foreground">{row.step}</span>
                    {row.details && <span className="text-muted-foreground sm:ml-2">— {row.details}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* Payment & History */}
      <div className="grid grid-cols-1 gap-5 pt-2 lg:grid-cols-12 lg:gap-6">
        {/* Payment Method */}
        <div className="space-y-3 lg:col-span-5 sm:space-y-4">
          <h3 className="px-0.5 text-lg font-black tracking-tight text-foreground sm:px-1 sm:text-xl">{t('subscription.paymentMethod')}</h3>
           <div className="group flex flex-col gap-4 rounded-2xl border border-border bg-card p-4 transition-all hover:border-foreground/20 sm:flex-row sm:items-center sm:justify-between sm:rounded-3xl sm:p-6">
              <div className="flex min-w-0 items-center gap-4 sm:gap-6">
                 <div className="flex h-11 w-14 shrink-0 items-center justify-center rounded-xl border border-border bg-muted text-muted-foreground sm:h-12 sm:w-16">
                    <CreditCard className="h-5 w-5 sm:h-6 sm:w-6" />
                 </div>
                 <div className="min-w-0 space-y-1">
                    <p className="font-black text-foreground">{t('subscription.cardEnding')}</p>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground sm:text-xs">{t('subscription.cardExpiry')}</p>
                 </div>
              </div>
              <button
                type="button"
                className="min-h-11 w-full rounded-xl border border-border bg-muted px-5 py-2.5 text-xs font-black text-foreground transition-all hover:bg-muted/80 sm:min-h-0 sm:w-auto sm:px-6"
              >
                  {t('subscription.update')}
              </button>
           </div>
        </div>

        {/* Billing History */}
        <div className="space-y-3 lg:col-span-7 sm:space-y-4">
           <div className="flex flex-col gap-2 px-0.5 sm:flex-row sm:items-center sm:justify-between sm:px-1">
              <h3 className="text-lg font-black tracking-tight text-foreground sm:text-xl">{t('subscription.billingHistory')}</h3>
              <button type="button" className="self-start text-sm font-black text-primary hover:underline decoration-2 underline-offset-4 sm:self-auto">
                {t('subscription.viewAll')}
              </button>
           </div>
           
           <div className="overflow-hidden rounded-2xl border border-border bg-card sm:rounded-3xl">
              {(invoices ?? []).length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-muted-foreground">{t('subscription.noInvoicesYet')}</p>
              ) : (
                <>
                  {!isBillingTableWide ? (
                  <div className="divide-y divide-border">
                    {(invoices ?? []).map((row) => (
                      <BillingInvoiceMobileRow
                        key={row.invoiceId}
                        row={row}
                        actionLoading={actionLoading}
                        onRefund={handleRequestRefund}
                        refundMailto={buildRefundMailto(
                          row,
                          user?.email,
                          t('subscription.requestRefundEmailSubject', { invoiceId: row.invoiceId }),
                          t('subscription.requestRefundEmailBody', {
                            invoiceId: row.invoiceId,
                            amount: formatCurrency(row.currency, row.amount),
                            date: invoiceDate(row.createdAt),
                            email: user?.email ?? '',
                          }),
                        )}
                      />
                    ))}
                  </div>
                  ) : (
                  <div>
                      <table className="w-full min-w-0 text-left">
                 <thead>
                    <tr className="border-b border-border bg-muted/20">
                      <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-[0.2em] text-muted-foreground md:px-6 md:py-4">{t('subscription.date')}</th>
                      <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-[0.2em] text-muted-foreground md:px-6 md:py-4">{t('subscription.amount')}</th>
                      <th className="px-4 py-3 text-right text-[11px] font-black uppercase tracking-[0.2em] text-muted-foreground md:px-6 md:py-4">{t('subscription.invoice')}</th>
                    </tr>
                 </thead>
                 <tbody className="divide-y divide-border">
                    {(invoices ?? []).map((row) => (
                       <tr key={row.invoiceId} className="transition-colors group hover:bg-muted/10">
                          <td className="px-4 py-3 text-sm font-bold text-muted-foreground transition-colors group-hover:text-foreground md:px-6 md:py-4">{invoiceDate(row.createdAt)}</td>
                          <td className="px-4 py-3 text-sm font-black text-foreground md:px-6 md:py-4">{formatCurrency(row.currency, row.amount)}</td>
                          <td className="px-4 py-3 text-right md:px-6 md:py-4">
                             {row.status === 'paid' && (
                               <a
                                 href={buildRefundMailto(
                                   row,
                                   user?.email,
                                   t('subscription.requestRefundEmailSubject', { invoiceId: row.invoiceId }),
                                   t('subscription.requestRefundEmailBody', {
                                     invoiceId: row.invoiceId,
                                     amount: formatCurrency(row.currency, row.amount),
                                     date: invoiceDate(row.createdAt),
                                     email: user?.email ?? '',
                                   }),
                                 )}
                                 className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-muted-foreground transition-colors hover:text-primary"
                               >
                                 {t('subscription.requestRefund')}
                               </a>
                             )}
                             {row.status === 'paid' && (
                               <button
                                 type="button"
                                 onClick={() => handleRequestRefund(row.invoiceId)}
                                 disabled={actionLoading}
                                 className="ml-3 inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-muted-foreground transition-colors hover:text-primary disabled:opacity-60 md:ml-4"
                               >
                                 {t('subscription.refund')}
                               </button>
                             )}
                             <button
                               type="button"
                               className="ml-3 inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-muted-foreground transition-colors hover:text-primary md:ml-4"
                             >
                                <Download size={14} />
                                {t('subscription.download')}
                             </button>
                          </td>
                       </tr>
                    ))}
                 </tbody>
              </table>
                  </div>
                  )}
                </>
              )}
           </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={pendingCheckout !== null}
        onClose={() => {
          if (!checkoutSubmitting) setPendingCheckout(null);
        }}
        title={t('subscription.mockCheckoutTitle')}
        isConfirming={checkoutSubmitting}
        onConfirm={() => void executeMockCheckout()}
        confirmLabel={t('subscription.mockCheckoutConfirm')}
        confirmingLabel={t('subscription.mockCheckoutWorking')}
        size="lg"
      >
        {pendingCheckout ? (
          <div className="space-y-4">
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t('subscription.mockCheckoutBody', {
                plan: pendingCheckout.planName,
                amount: formatCurrency(pendingCheckout.currency, pendingCheckout.amount),
                suffix:
                  pendingCheckout.billingCycle === 'yearly'
                    ? t('subscription.perYear')
                    : t('subscription.perMonth'),
              })}
            </p>
            <p className="rounded-xl border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-xs text-muted-foreground">
              {t('subscription.mockCheckoutBetaNote')}
            </p>
          </div>
        ) : null}
      </ConfirmModal>

      <ConfirmModal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        title={t('subscription.cancelDialogTitle')}
        isConfirming={cancelSubmitting || pauseSubmitting || retentionSubmitting}
        onConfirm={() => void executeCancelAtPeriodEnd()}
        confirmLabel={t('subscription.cancelConfirm')}
        confirmingLabel={t('subscription.cancelWorking')}
      >
        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-muted-foreground">{t('subscription.cancelDialogBody')}</p>
          {(subscription?.planId === 2 || subscription?.planId === 3) && (
            <button
              type="button"
              disabled={loading || actionLoading}
              onClick={() => void switchToProFromCancelModal()}
              className="w-full rounded-xl bg-primary py-2.5 text-sm font-black text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
            >
              {t('subscription.switchToProInstead')}
            </button>
          )}
          {subscription != null &&
            subscription.planId > 0 &&
            (subscription.retentionCouponExpiresAt == null ||
              subscription.retentionCouponExpiresAt <= Date.now()) && (
              <button
                type="button"
                disabled={retentionSubmitting || loading || actionLoading}
                onClick={() => void executeAcceptRetentionDiscount()}
                className="w-full rounded-xl border border-emerald-500/40 bg-emerald-500/10 py-2.5 text-sm font-black text-emerald-700 transition-colors hover:bg-emerald-500/20 dark:text-emerald-400 disabled:opacity-60"
              >
                {retentionSubmitting ? t('subscription.retentionWorking') : t('subscription.acceptRetentionDiscount')}
              </button>
            )}
          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {t('subscription.cancelSurveyLabel')}
            </span>
            <textarea
              value={cancelSurveyReason}
              onChange={(e) => setCancelSurveyReason(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-offset-background placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
              placeholder={t('subscription.cancelSurveyPlaceholder')}
            />
          </label>
          <label className="flex cursor-pointer items-start gap-3 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={retentionOfferAccepted}
              onChange={(e) => setRetentionOfferAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-border"
            />
            <span>{t('subscription.retentionOffer')}</span>
          </label>
          <div className="space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {t('subscription.pauseMonthsLabel')}
            </span>
            <select
              value={pauseMonths}
              onChange={(e) => setPauseMonths(Number(e.target.value) as 1 | 2 | 3)}
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm font-semibold text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value={1}>{t('subscription.pauseMonth1')}</option>
              <option value={2}>{t('subscription.pauseMonth2')}</option>
              <option value={3}>{t('subscription.pauseMonth3')}</option>
            </select>
          </div>
          <button
            type="button"
            disabled={pauseSubmitting || cancelSubmitting || retentionSubmitting || loading || actionLoading}
            onClick={() => void executePauseWithSelectedMonths()}
            className="w-full rounded-xl border border-border py-2.5 text-sm font-black text-foreground transition-colors hover:bg-muted disabled:opacity-60"
          >
            {pauseSubmitting ? t('subscription.pauseWorking') : t('subscription.pauseInstead')}
          </button>
        </div>
      </ConfirmModal>
    </div>
  );
};
